import type { BaseMarkup } from '@features/quotation-intake/types/quotationIntake.types';
import { PriceLevel, type QuotationLineItemDto } from '../types/quotation.types';
import { isPostedRow } from './lineItem';

/**
 * Fallback VAT rate used when the selected customer has no tax rate synced
 * from NetSuite (e.g. the customer's taxitem reference is unset).
 * Decimal form — 0.12 = 12%.
 */
export const DEFAULT_VAT_RATE = 0.12;

// ── Rebar size category (14ykpqtr22u) ────────────────────────────────────────
// Owner rule 2026-09-28: 16mm is a BIG size. Mirrors CSC_CORE's RebarSizeConstants —
// scripts/rebarSizeParity.test.ts fails if the lists or the version drift apart.
// Going forward only: posted records keep the rule they were posted under.

/** Diameters (mm) classified as "small" for the size-mix rule (the current rule). */
export const SMALL_DIAMETERS_MM: readonly number[] = [10, 12];

/** The rule before 2026-09-28 — used only to display records posted under it. */
export const LEGACY_SMALL_DIAMETERS_MM: readonly number[] = [10, 12, 16];

/** Quick-select chips: a sized row at or above this is "Big". */
export const BIG_DIAMETER_MIN_MM = 16;

/**
 * Sent with every cutting-list submit; the server refuses a mismatch (a tab left open across a
 * deploy). Bump together with RebarSizeConstants.PricingRuleVersion whenever the rule changes.
 */
export const PRICING_RULE_VERSION = 2;

/** A size rule: which diameters count as small, and where "big" starts for captions. */
export interface SizeRule {
  smallDiametersMm: readonly number[];
  bigMinMm: number;
}

export const CURRENT_SIZE_RULE: SizeRule = { smallDiametersMm: SMALL_DIAMETERS_MM, bigMinMm: 16 };
export const LEGACY_SIZE_RULE: SizeRule = { smallDiametersMm: LEGACY_SMALL_DIAMETERS_MM, bigMinMm: 20 };

/**
 * Decodes the size rule recorded on a posted snapshot. A list equal to the current rule →
 * CURRENT_SIZE_RULE; anything else (missing, null, [], not an array, the legacy list) →
 * LEGACY_SIZE_RULE, because every record written before the rule was recorded used it.
 * The next rule change must add its list here.
 */
export function sizeRuleFromRecorded(list: unknown): SizeRule {
  if (
    Array.isArray(list) &&
    list.length === SMALL_DIAMETERS_MM.length &&
    SMALL_DIAMETERS_MM.every((d) => list.some((x) => Number(x) === d))
  ) {
    return CURRENT_SIZE_RULE;
  }
  return LEGACY_SIZE_RULE;
}

/** Quick-select chips split at BIG_DIAMETER_MIN_MM: every sized row lands in exactly one chip. */
export function isSmallChipDiameter(diameterMm: number): boolean {
  return diameterMm < BIG_DIAMETER_MIN_MM;
}

/**
 * Returns the markup percentage for a given grade code, or null if no mapping
 * exists (e.g. Grade 33, Grade 50 — the base-markup record only defines g40/g60/g75/g80).
 */
export function markupForGrade(gradeCode: string | null | undefined, markup: BaseMarkup | null): number | null {
  if (!markup || !gradeCode) return null;
  switch (gradeCode) {
    case '40': return markup.g40;
    case '60': return markup.g60;
    case '75': return markup.g75;
    case '80': return markup.g80;
    default:   return null;
  }
}

// --- Tier / Size-mix adjustment matrix ---

export type PricingTier = 'I' | 'II' | 'III';
export type PricingSizeMix = 'A' | 'B' | 'C';

/**
 * Per-grade markup adjustment (percentage points as decimal) added on top of
 * the base markup, determined by total-weight tier × size-mix. Matches the
 * pricing criteria reference table.
 */
const TIER_ADJUSTMENT: Record<PricingTier, Record<PricingSizeMix, number>> = {
  I:   { A: 0.04, B: 0.02, C: 0.00 },
  II:  { A: 0.06, B: 0.04, C: 0.02 },
  III: { A: 0.08, B: 0.06, C: 0.04 },
};

export interface PricingContext {
  tier: PricingTier;
  sizeMix: PricingSizeMix;
  totalWeightKg: number;
  /** Small-item weight ÷ total weight, in [0, 1]. */
  smallPct: number;
  /** Percentage points as decimal (e.g. 0.04 = +4%) added to base per-grade markup. */
  adjustment: number;
}

/**
 * Computes the cutting-list's total weight + small%, then picks the
 * tier/size-mix cell from the criteria matrix. Returns null when the draft
 * has no weighted line items yet (in which case pricing falls back to the
 * raw base-markup with no adjustment).
 *
 * 14ykpqtr22u — only rows that post count (isPostedRow), exactly what the server prices from;
 * `rule` is the size rule to classify with (the current one unless a record says otherwise).
 */
export function computePricingContext(
  lineItems: readonly QuotationLineItemDto[],
  rule: SizeRule = CURRENT_SIZE_RULE,
): PricingContext | null {
  let totalWeight = 0;
  let smallWeight = 0;

  for (const li of lineItems) {
    if (!isPostedRow(li)) continue;
    const weight = li.weightKg ?? 0;
    if (weight <= 0) continue;
    totalWeight += weight;
    if (li.diameterMm != null && rule.smallDiametersMm.includes(li.diameterMm)) {
      smallWeight += weight;
    }
  }

  if (totalWeight <= 0) return null;

  const smallPct = smallWeight / totalWeight;

  const tier: PricingTier =
    totalWeight >= 500_000 ? 'I' : totalWeight >= 40_000 ? 'II' : 'III';

  const sizeMix: PricingSizeMix =
    smallPct >= 0.75 ? 'A' : smallPct > 0.30 ? 'B' : 'C';

  return {
    tier,
    sizeMix,
    totalWeightKg: totalWeight,
    smallPct,
    adjustment: TIER_ADJUSTMENT[tier][sizeMix],
  };
}

export interface WeightBreakdownRow {
  diameter: number;
  weight: number;
  /** This diameter's share of the total weight, in [0, 1]. */
  pct: number;
  isSmall: boolean;
}

export interface WeightBreakdown {
  total: number;
  /** Weight of rows with no diameter. */
  unspecified: number;
  rows: WeightBreakdownRow[];
  smallWeight: number;
  bigWeight: number;
  smallPct: number;
  bigPct: number;
  /** Small card caption, derived from the rule — e.g. "10/12mm". */
  smallCaption: string;
  /** Big card caption — "≥16mm" (per the rule), or "other sizes" when a non-small row sits below it (6/8mm). */
  bigCaption: string;
}

/**
 * 14ykpqtr22u — the Weight Breakdown shared by the quotation table and the SO panel: weight by
 * diameter plus the Small/Big split under `rule`. Pure — callers choose the rows (a draft's posted
 * rows, a posted snapshot's lines, an SO's lines) and the rule (current, or the one a record was
 * posted under), so the captions can never contradict the numbers.
 */
export function computeWeightBreakdown(
  rows: ReadonlyArray<{ diameterMm: number | null; weightKg: number | null }>,
  rule: SizeRule,
): WeightBreakdown {
  const byDiameter = new Map<number, number>();
  let unspecified = 0;
  let total = 0;
  let smallWeight = 0;
  let bigWeight = 0;
  let bigBelowMin = false;
  for (const r of rows) {
    const w = r.weightKg ?? 0;
    if (w <= 0) continue;
    total += w;
    if (r.diameterMm == null) {
      unspecified += w;
      continue;
    }
    byDiameter.set(r.diameterMm, (byDiameter.get(r.diameterMm) ?? 0) + w);
    if (rule.smallDiametersMm.includes(r.diameterMm)) {
      smallWeight += w;
    } else {
      bigWeight += w;
      if (r.diameterMm < rule.bigMinMm) bigBelowMin = true;
    }
  }
  const breakdownRows = Array.from(byDiameter.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([diameter, weight]) => ({
      diameter,
      weight,
      pct: total > 0 ? weight / total : 0,
      isSmall: rule.smallDiametersMm.includes(diameter),
    }));
  return {
    total,
    unspecified,
    rows: breakdownRows,
    smallWeight,
    bigWeight,
    smallPct: total > 0 ? smallWeight / total : 0,
    bigPct: total > 0 ? bigWeight / total : 0,
    smallCaption: `${rule.smallDiametersMm.join('/')}mm`,
    bigCaption: bigBelowMin ? 'other sizes' : `≥${rule.bigMinMm}mm`,
  };
}

export interface PricingBreakdown {
  /** Per-piece base rate (from item's Base or Average price matrix). */
  rate: number;
  /** Effective markup applied (base-per-grade + tier/size-mix adjustment), as a decimal. */
  markupPct: number;
  /** Per-piece rate after markup: `rate × (1 + markupPct)`. */
  rateWithMarkup: number;
  /** Line total: `rateWithMarkup × quantity`. */
  amount: number;
  /** VAT-inclusive line total: `amount × 1.12`. */
  grossAmount: number;
}

/**
 * Returns the full pricing breakdown for a cutting-list line, or null when any
 * of these hold (in which case all pricing cells render as an em-dash):
 *   - unit is 'kgs' (kgs rows belong to the future Firm Offer form)
 *   - priceLevel is Custom (flag only; no computed amount)
 *   - quantity is null
 *   - item isn't matched (no base/average price snapshot)
 *   - grade has no markup column in CUSTOMRECORD_BASEMARKING_VALUES
 *   - base-markup service returned null
 *
 * `adjustment` is the tier/size-mix bump (as a decimal) added on top of the
 * base per-grade markup. Default 0 when no context is computed yet.
 *
 * `taxRate` is the customer-specific VAT rate (decimal, e.g. 0.12) used for
 * Gross Amount. Null/undefined falls back to `DEFAULT_VAT_RATE`.
 */
export function computePricing(
  li: QuotationLineItemDto,
  markup: BaseMarkup | null,
  adjustment: number = 0,
  taxRate: number | null = null,
): PricingBreakdown | null {
  if (li.unit !== 'pcs') return null;
  if (li.priceLevel === PriceLevel.Custom) return null;
  if (li.quantity == null) return null;

  const rate =
    li.priceLevel === PriceLevel.AveragePrice ? li.averagePricePerPc : li.basePricePerPc;
  if (rate == null) return null;

  const base = markupForGrade(li.gradeCode, markup);
  if (base == null) return null;

  const effectivePct = base + adjustment;
  const rateWithMarkup = rate * (1 + effectivePct);
  const amount = rateWithMarkup * li.quantity;
  const effectiveTax = taxRate ?? DEFAULT_VAT_RATE;
  return {
    rate,
    markupPct: effectivePct,
    rateWithMarkup,
    amount,
    grossAmount: amount * (1 + effectiveTax),
  };
}

const phpFormatter = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatPhp(value: number | null | undefined): string {
  if (value == null) return '—';
  return phpFormatter.format(value);
}

/** Renders a markup decimal like 0.865 as "+86.5%" (trailing zeros trimmed). */
export function formatMarkupPct(pct: number): string {
  const percent = Number((pct * 100).toFixed(2));
  return `+${percent}%`;
}

/** Renders a weight in kg as "12.5 mt" (≥ 1000 kg) or "987 kg" (smaller). */
export function formatWeight(kg: number): string {
  if (kg >= 1000) {
    const tons = kg / 1000;
    return `${Number(tons.toFixed(tons >= 100 ? 0 : tons >= 10 ? 1 : 2))} mt`;
  }
  return `${Math.round(kg).toLocaleString()} kg`;
}

/** Renders a 0..1 fraction as a whole-percent string: 0.482 → "48%". */
export function formatSmallPct(pct: number): string {
  return `${Math.round(pct * 100)}%`;
}

// Per-diameter color palette for the weight-breakdown bar/legend (shared by the
// Quotation and Sales Order line-item tables). Cool→warm progression so smaller
// bars sit on the left and read distinctly. Full class names are required for
// Tailwind's JIT.
// Categorical data-viz colors (UI-guidelines exception). The solid bar/swatch
// blocks read fine on any surface; only the legend TEXT needs a lighter shade in
// dark mode (the 700 shade blends into a dark canvas). Industrial is light-based,
// so it keeps the 700 shade — no dark: variant fires there.
export const DIAMETER_COLOR_MAP: Record<number, { bar: string; swatch: string; text: string }> = {
  10: { bar: 'bg-sky-400',     swatch: 'bg-sky-400',     text: 'text-sky-700 dark:text-sky-300' },
  12: { bar: 'bg-cyan-500',    swatch: 'bg-cyan-500',    text: 'text-cyan-700 dark:text-cyan-300' },
  16: { bar: 'bg-blue-500',    swatch: 'bg-blue-500',    text: 'text-blue-700 dark:text-blue-300' },
  20: { bar: 'bg-indigo-500',  swatch: 'bg-indigo-500',  text: 'text-indigo-700 dark:text-indigo-300' },
  25: { bar: 'bg-violet-500',  swatch: 'bg-violet-500',  text: 'text-violet-700 dark:text-violet-300' },
  28: { bar: 'bg-fuchsia-500', swatch: 'bg-fuchsia-500', text: 'text-fuchsia-700 dark:text-fuchsia-300' },
  32: { bar: 'bg-pink-500',    swatch: 'bg-pink-500',    text: 'text-pink-700 dark:text-pink-300' },
  36: { bar: 'bg-rose-500',    swatch: 'bg-rose-500',    text: 'text-rose-700 dark:text-rose-300' },
  40: { bar: 'bg-orange-500',  swatch: 'bg-orange-500',  text: 'text-orange-700 dark:text-orange-300' },
};
export const DIAMETER_COLOR_FALLBACK = {
  bar: 'bg-slate-500',
  swatch: 'bg-slate-500',
  text: 'text-slate-700 dark:text-slate-300',
};

export function colorsForDiameter(d: number) {
  return DIAMETER_COLOR_MAP[d] ?? DIAMETER_COLOR_FALLBACK;
}

// ── 86d32t157 Phase 3 — pricing-drift baseline ──────────────────────────────
// What the client computed at save time. Persisted server-side as
// Quotation.PricingSnapshotJson and read back at submit as the comparison
// baseline. Drift past 0.0001 produces HTTP 409 PRICING_DRIFT.

export interface PricingSnapshotLine {
  /** NetSuite item internalId. Consolidated by item id post-line-items table. */
  matchedItemId: string;
  /** Server fills this in on save (BracketId derived from rate + finalUnitPrice
   *  against the bracket cache). Client sends null. */
  bracketId: string | null;
  finalUnitPrice: number;
  rate: number;
}

export interface PricingSnapshot {
  baseMarkup: { g40: number; g60: number; g75: number; g80: number };
  tier: string | null;
  sizeMix: string | null;
  tierAdjustmentPct: number | null;
  lines: PricingSnapshotLine[];
}

/**
 * Builds the pricing snapshot from the current draft + the panel-mount
 * baseMarkup. Consolidates lines by matchedItemId so the shape matches the
 * submit request's consolidation. Returns null when there's no usable data
 * (no baseMarkup, no priced lines) — the save still goes through without a
 * snapshot, and submit's drift check tolerates a missing baseline on legacy
 * drafts.
 */
export function buildPricingSnapshot(
  lineItems: QuotationLineItemDto[],
  baseMarkup: BaseMarkup | null,
): PricingSnapshot | null {
  if (!baseMarkup) return null;

  const ctx = computePricingContext(lineItems);
  const adjustment = ctx?.adjustment ?? 0;

  // Consolidate by matched item id: same item ⇒ same price. Use the first
  // breakdown we successfully compute per id; subsequent rows with the same
  // matchedItemId would produce identical values anyway.
  const linesByItem = new Map<string, PricingSnapshotLine>();
  for (const li of lineItems) {
    if (li.unit !== 'pcs') continue;
    if (!li.matchedItemId) continue;
    if (linesByItem.has(li.matchedItemId)) continue;
    const breakdown = computePricing(li, baseMarkup, adjustment);
    if (!breakdown) continue;
    linesByItem.set(li.matchedItemId, {
      matchedItemId: li.matchedItemId,
      bracketId: null, // server fills in
      finalUnitPrice: breakdown.rateWithMarkup,
      rate: breakdown.rate,
    });
  }

  return {
    baseMarkup: {
      g40: baseMarkup.g40,
      g60: baseMarkup.g60,
      g75: baseMarkup.g75,
      g80: baseMarkup.g80,
    },
    tier: ctx?.tier ?? null,
    sizeMix: ctx?.sizeMix ?? null,
    tierAdjustmentPct: ctx?.adjustment ?? null,
    lines: Array.from(linesByItem.values()),
  };
}
